import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo, useState } from "react";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { PayerRowsQuery } from "../../../services/search";
import { selectorIsEngagementsActiveForPayer } from "../../Payees/selectors/selectorIsEngagementsActiveForPayer";
import {
  selectorPayerEngagementNameTmp,
  selectorPayerEngagementsTmp
} from "../../Payees/selectors/selectorPayerEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../Payees/selectors/selectorPayerPayeeEngagementName";
import { selectorPayerPayeeEngagements } from "../../Payees/selectors/selectorPayerPayeeEngagements";

/**
 * Custom hook for providing API-based payer engagement filter options
 * with pagination and search support.
 *
 * @returns WSFilterApiOptionsLoader configuration for WSFilters
 *
 * @example
 * const engagementOptions = usePayerEngagementFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Engagement",
 *     name: "engagementId",
 *     type: "select",
 *     apiOptions: engagementOptions
 *   }
 * ];
 */
export function usePayerEngagementFilterOptions(
  baseQuery?: PayerRowsQuery
): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = useQueryPayerRows({
    ...baseQuery,
    filter: {
      ...baseQuery?.filter,
      searchString: searchText || undefined
    }
  });

  const options = useMemo(() => {
    const result: WSFilterOption<"checkbox">[] = [];

    (query.data || []).forEach(payer => {
      if (!payer.user) return;

      const payerNames = wsName({
        user: payer.user,
        member: payer.member,
        payeeOwnedData: payer.payeeOwnedData
      });

      const payerName = payerNames.getResolvedName();

      const isEngagementsActiveForPayer = selectorIsEngagementsActiveForPayer(
        payer.engagements
      );

      const filteredEngagements = isEngagementsActiveForPayer
        ? selectorPayerPayeeEngagements(payer.engagements, true)
        : selectorPayerEngagementsTmp(payer.engagements, false, false);

      filteredEngagements.forEach((engagement, _index, all) => {
        const engagementName = isEngagementsActiveForPayer
          ? selectorPayerPayeeEngagementName(engagement, false)
          : all.length > 1
          ? selectorPayerEngagementNameTmp(engagement, true)
          : undefined;

        result.push({
          name: engagement.payerPayeeEngagementId,
          label: [payerName, engagementName].filter(Boolean).join(" • "),
          helperText: undefined
        });
      });
    });

    return result;
  }, [query.data]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
